import { useRef } from "react";
import { DollarSignIcon } from "lucide-react";

import halloweenBackdrop from "@/assets/racetrack/halloween-backdrop-playful.png";
import mostRevBox from "@/assets/racetrack/Most Rev Box.svg";
import { Column, Row, Text } from "@/components";
import { HexagonIconBadge } from "@/components/leaderboard/HexagonIconBadge";
import { LeaderPetName } from "@/components/leaderboard/LeaderPetName";
import { MetricLeadersHoverCard } from "@/components/leaderboard/MetricLeadersHoverCard";
import { RevenueRacetrack } from "@/components/leaderboard/RevenueRacetrack";
import { Card, CardContent } from "@/components/ui/card";
import {
  useInactiveUserIds,
  useTaggableUsers,
} from "@/contexts/TaggableUsersContext";
import { useGetLeaderboard } from "@/hooks/queries/useGetLeaderboard";
import { useGetPets } from "@/hooks/queries/useGetPets";
import { useGetTeamGoal } from "@/hooks/queries/useGetTeamGoal";
import { IS_LEADERBOARD_HALLOWEEN, REVENUE_GRADIENT } from "@/lib/constants";
import { formatting } from "@/lib/formatting";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import {
  leaderboardGoal,
  type RacetrackGoalScope,
} from "@/lib/leaderboardGoal";
import { teamGoals } from "@/lib/teamGoals";

import {
  TeamGoalField,
  UserRole,
  type LeaderboardQueryParams,
  type LeaderboardUser,
  type Pet,
  type TeamGoal,
} from "@/types";

// Floor so a short field still shows the backdrop scene.
const HALLOWEEN_MIN_CARD_HEIGHT_PX = 409;

// Per-staffer revenue goal for the finish line: an even share of the month's
// team goal, scaled to a week when the selected period is weekly.
const perStafferRevenueGoal = (
  teamGoal: TeamGoal | null | undefined,
  scope: RacetrackGoalScope | null,
): number | undefined => {
  if (!teamGoal || teamGoal.numStaffers <= 0 || scope === null)
    return undefined;

  const monthly = teamGoals.perStafferGoal(
    teamGoal.revenueGoal,
    teamGoal.numStaffers,
    TeamGoalField.REVENUE,
  );
  if (scope === "WEEK") {
    return teamGoals.weeklyGoal(monthly, TeamGoalField.REVENUE);
  }
  return monthly;
};

export const MostRevenueAndRacetrackContainer = ({
  queryParams,
}: {
  queryParams: LeaderboardQueryParams;
}) => {
  /* React Hooks */
  const { users: taggableUsers } = useTaggableUsers();
  const inactiveUserIds = useInactiveUserIds();
  const { data: pets } = useGetPets();
  const resolution = leaderboardGoal.resolvePeriod(queryParams);

  // The race always reflects the selected period; ranges wider than a month
  // just drop the goal (see leaderboardGoal) and race to the top staffer.
  const { data: raceData, isPlaceholderData } = useGetLeaderboard(queryParams);

  // Wait for staffer's  data to refresh before refreshing goal
  const committedResolution = useRef(resolution);
  if (!isPlaceholderData) committedResolution.current = resolution;
  const activeResolution = committedResolution.current;

  const { data: teamGoal } = useGetTeamGoal(
    activeResolution.goalMonth,
    activeResolution.goalYear,
  );

  /* Helpers */
  const activeStafferIds = new Set(
    taggableUsers
      .filter((u) => u.deletedAt === null && u.userRole === UserRole.STAFFER)
      .map((u) => u.id),
  );
  // Deactivated staffers still race if they earned revenue this period
  // (e.g. a placement closed before they were deleted).
  const racers = raceData
    ? raceData.filter((u) => activeStafferIds.has(u.userId) || u.revenue > 0)
    : [];

  // Each lane's racer — missing entry when the staffer hasn't adopted a pet.
  const petByUserId = new Map<number, Pet>(
    (pets ?? []).map((pet) => [pet.userId, pet]),
  );

  const revenueGoalPerStaffer = perStafferRevenueGoal(
    teamGoal,
    activeResolution.goalScope,
  );

  const maxRevenue = Math.max(...racers.map((u) => u.revenue), 0);
  const leaders = racers
    .filter((u) => u.revenue === maxRevenue && maxRevenue > 0)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));

  /* Render */
  const renderRevenueHoverBadge = (backdropColor?: string) => (
    <MetricLeadersHoverCard
      title="Most Revenue"
      icon={DollarSignIcon}
      gradient={REVENUE_GRADIENT}
      leaders={leaderboardMetrics.topN(racers, "revenue", 3)}
      valueOf={(u) => u.revenue}
      format={formatting.currency}
      isHalloween={IS_LEADERBOARD_HALLOWEEN}
      badgeBackdropColor={backdropColor}
    >
      <Row className="cursor-default">
        <HexagonIconBadge
          icon={DollarSignIcon}
          gradient={REVENUE_GRADIENT}
          backdropColor={backdropColor}
        />
      </Row>
    </MetricLeadersHoverCard>
  );

  const renderRacetrack = () => (
    <RevenueRacetrack
      users={racers}
      revenueGoalPerStaffer={revenueGoalPerStaffer}
      goalScope={activeResolution.goalScope}
      isHalloween={IS_LEADERBOARD_HALLOWEEN}
      petByUserId={petByUserId}
      inactiveUserIds={inactiveUserIds}
    />
  );

  // Halloween scene: the hero stats sit top-left on a translucent panel and
  // the track runs over the darkened lower band.
  const renderHalloween = () => {
    return (
      <Card
        className="relative flex flex-col overflow-hidden rounded-3xl border-0 bg-black shadow-sm"
        style={{ minHeight: HALLOWEEN_MIN_CARD_HEIGHT_PX }}
      >
        <img
          src={halloweenBackdrop}
          alt=""
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
        />
        <CardContent className="relative z-[1] flex flex-1 flex-col p-0">
          <Row className="items-start justify-between gap-3 px-7 pt-6">
            <Column className="min-w-0 gap-1.5 rounded-xl bg-black/40 px-4 py-3">
              <Text className="text-sm font-semibold uppercase leading-tight tracking-wider text-slate-200">
                Most Revenue
              </Text>
              <Row className="min-w-0 items-center gap-6">
                <Text className="shrink-0 text-4xl font-bold tabular-nums tracking-tight text-white">
                  {maxRevenue > 0 ? formatting.currency(maxRevenue) : "—"}
                </Text>
                <RevenueHeroLeaders leaders={leaders} isHalloween />
              </Row>
            </Column>
            {renderRevenueHoverBadge("#d5f7df")}
          </Row>
          <Row className="mt-auto bg-gradient-to-b from-transparent via-black/20 to-black/45 px-12 pb-4 pt-8">
            {renderRacetrack()}
          </Row>
        </CardContent>
      </Card>
    );
  };

  const renderClassic = () => (
    <Card className="relative min-h-[15rem] overflow-hidden rounded-2xl border-[#8fbf97]/70 bg-[#A8D5A2] shadow-sm dark:border-[#77787A] dark:bg-[#4C4F52]">
      <img
        src={mostRevBox}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover dark:opacity-10"
      />
      <CardContent className="relative z-[1] p-0">
        <Column className="gap-0 overflow-hidden">
          <Column className="gap-2.5 px-7 pb-5 pt-6">
            <Row className="items-center justify-between gap-3">
              <Text className="text-sm font-semibold uppercase leading-tight tracking-wider text-slate-800">
                Most Revenue
              </Text>
              {renderRevenueHoverBadge()}
            </Row>
            <Row className="min-w-0 items-center gap-10">
              <Text className="shrink-0 text-4xl font-bold tabular-nums tracking-tight text-slate-900">
                {maxRevenue > 0 ? formatting.currency(maxRevenue) : "—"}
              </Text>
              <RevenueHeroLeaders leaders={leaders} />
            </Row>
          </Column>
          <Row className="bg-[#616161] px-7 py-5 dark:bg-[#212327]">
            {renderRacetrack()}
          </Row>
        </Column>
      </CardContent>
    </Card>
  );

  const render = () => {
    if (IS_LEADERBOARD_HALLOWEEN) return renderHalloween();
    return renderClassic();
  };

  return render();
};

const RevenueHeroLeaders = ({
  leaders,
  isHalloween = false,
}: {
  leaders: LeaderboardUser[];
  isHalloween?: boolean;
}) => {
  const render = () => {
    if (leaders.length === 0) {
      return (
        <Text
          className={
            isHalloween
              ? "text-lg font-medium text-slate-300"
              : "text-lg font-medium text-slate-400"
          }
        >
          —
        </Text>
      );
    }

    return (
      <Row className="min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
        {leaders.map((leader) => (
          <LeaderPetName
            key={leader.userId}
            duneWormPetSizePx={90}
            leader={leader}
            petSizePx={56}
            textClassName={
              isHalloween
                ? "text-lg font-semibold text-white"
                : "text-lg font-semibold text-slate-900"
            }
          />
        ))}
      </Row>
    );
  };

  return render();
};
